import { useCtxStore } from '@/stores/ctx.store'
import consola from 'consola'
import type { FinancialsGridDataAvailablePlanList } from '~/types/stcs_fpna_res_values.entity'
import { ref, watch } from '#imports'

interface KpiLoadParams {
  orgId: string | undefined
  compId: string | undefined
  planId: string | undefined
  date: string | undefined
}

interface UseKpiLoaderOptions {
  activeCompId: () => string
  activeOrgId: () => string
  selectedPlan: () => Record<string, FinancialsGridDataAvailablePlanList | undefined>
  lastActualsDate: () => Record<string, string>
  forceUpdateOnMount?: boolean
}

/**
 * Composable for handling KPI data loading with deduplication.
 * Manages separate tracking for KPI Res and KPI Config to avoid redundant API calls.
 */
export function useKpiLoader(options: UseKpiLoaderOptions) {
  const { activeCompId, activeOrgId, selectedPlan, lastActualsDate, forceUpdateOnMount = true } = options
  const ctxStore = useCtxStore()
  const router = useRouter()

  let debounceTimeout: ReturnType<typeof setTimeout> | null = null

  // Track previous KPI load params separately for each load type
  const previousKpiResParams = ref<KpiLoadParams>({
    orgId: undefined,
    compId: undefined,
    planId: undefined,
    date: undefined,
  })

  const previousKpiCfgParams = ref<KpiLoadParams>({
    orgId: undefined,
    compId: undefined,
    planId: undefined,
    date: undefined,
  })

  /**
   * Checks if KPI load params have changed compared to previous values.
   * @param isCfg - true for KPI Config, false for KPI Res
   */
  function hasKpiParamsChanged(orgId: string | undefined, compId: string | undefined, planId: string | undefined, date: string | undefined, isCfg = false): boolean {
    const prev = isCfg ? previousKpiCfgParams.value : previousKpiResParams.value
    return prev.orgId !== orgId || prev.compId !== compId || prev.planId !== planId || prev.date !== date
  }

  /**
   * Updates the stored previous KPI params after a successful check.
   * @param isCfg - true for KPI Config, false for KPI Res
   */
  function updatePreviousKpiParams(orgId: string | undefined, compId: string | undefined, planId: string | undefined, date: string | undefined, isCfg = false): void {
    if (isCfg) {
      previousKpiCfgParams.value = { orgId, compId, planId, date }
    } else {
      previousKpiResParams.value = { orgId, compId, planId, date }
    }
  }

  /**
   * Initializes the KPI loading watcher.
   * Watches for changes in company, org, plan, date, and route to trigger KPI loads.
   *
   * Important for new-org correctness:
   * - We now *always* drive the cfg calc path (isCfg=true) as soon as has_data is true for a comp.
   *   The cfg calc (cmwCompKpiCfgCalcRequest) is the prerequisite the backend needs before res values
   *   are populated. Previously this was gated behind analysis/kpi routes only, so a fresh org's
   *   first dashboard load would only ever call the res path (often with a bad planId) and fail.
   * - Res values stay primarily route-driven (dashboard needs the numbers).
   * - planId is sanitized against the comp's availablePlanList before any load.
   */
  function initKpiLoadWatcher() {
    watch(
      [() => ctxStore.ref_comp_map?.[activeCompId()]?.has_data, activeCompId, activeOrgId, selectedPlan, lastActualsDate, () => router.currentRoute.value.path],
      // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
      ([hasData, compId, orgId, planMap, dateMap, currentPath]) => {
        // Use the currentPath from the watch dependency to ensure we have the latest route
        const routePath = currentPath || router.currentRoute.value.path

        const kpiResRoutes = ['/dashboard/', '/analysis/monthly/nwc', '/analysis/yearly/nwc']
        // cfg calc is no longer strictly route-gated (see comment above) — we decide per-comp below.

        if (!(compId && orgId && hasData)) {
          return
        }

        let planId = planMap[compId]?.id
        const date = dateMap[compId]
        const shouldForceLoad = forceUpdateOnMount

        // Sanitize planId: only use it if it actually exists in the current available plans for the comp.
        // Prevents sending bogus values like "0" (seen on fresh orgs before plan selection stabilizes).
        const availPlans = ctxStore.ref_comp_map[compId]?.availablePlanList || []
        if (planId && !availPlans.some((p) => p.id === planId)) {
          consola.debug(`🟡 useKpiLoader: planId ${planId} not present in availablePlanList for ${compId}, omitting from KPI request`)
          planId = undefined
        }

        // Always attempt KPI *config* calculation for the active comp as soon as it has data.
        // This is the "setup" step (cmwCompKpiCfgCalcRequest) that the backend needs before
        // meaningful res values can be produced for a new org/comp. We do NOT gate it on specific
        // analysis routes — the old route gate meant a pure dashboard load after org creation
        // would never trigger the cfg calc, leaving ref_comp_kpis_res empty/broken.
        const shouldLoadKpicfg = shouldForceLoad || hasKpiParamsChanged(orgId, compId, planId, date, true)

        // Res values are still primarily needed on dashboard (and a few NWC analysis views).
        const loadKpiRes = kpiResRoutes.some((route) => routePath.includes(route))
        const shouldLoadKpiRes = loadKpiRes && (shouldForceLoad || hasKpiParamsChanged(orgId, compId, planId, date))

        // Skip only if neither is needed
        if (!(shouldLoadKpiRes || shouldLoadKpicfg)) {
          consola.debug('🟡 useKpiLoader: Skipping KPI load - params unchanged')
          return
        }

        if (debounceTimeout) {
          clearTimeout(debounceTimeout)
        }

        debounceTimeout = setTimeout(() => {
          if (shouldLoadKpicfg) {
            updatePreviousKpiParams(orgId, compId, planId, date, true)
            ctxStore.loadKpiResValues(orgId, compId, planId, date, true).subscribe({
              next: () => consola.debug(`✅ KPI Config (calc) loaded for plan ${planId || '(none)'}.`),
              error: (err) => consola.error(`🔴 Failed to load KPI Config for plan ${planId || '(none)'}:`, err),
            })
          }

          if (shouldLoadKpiRes) {
            updatePreviousKpiParams(orgId, compId, planId, date)
            ctxStore.loadKpiResValues(orgId, compId, planId, date).subscribe({
              next: () => consola.debug(`✅ KPI Res loaded for plan ${planId || '(none)'}.`),
              error: (err) => consola.error(`🔴 Failed to load KPI Res for plan ${planId || '(none)'}:`, err),
            })
          }
        }, 800) // 800ms debounce delay
      },
      { immediate: true, deep: true },
    )
  }

  return {
    initKpiLoadWatcher,
  }
}
